import { LoadingSpinnerContainer } from 'component/progress/loading-spinner';
import { useLanguage } from 'component/translation';
import { useIsSuspended } from 'data/suspensions';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { useEffect } from 'react';
import { LoginBlockedScreen } from 'structure/login';
import { useSession } from 'util/auth-client';
import { usePersonaKey } from 'util/datastore';

/**
 * Where a suspended user is sent from any app page. Once the user signs out or
 * is no longer suspended, `/suspended` goes back to the page that sent them
 * here. With nothing to go back to, it sends a signed-out user to login and
 * anyone else to their account page, as the login screen does.
 */
export default function SuspendedScreen() {
    const { siloKey } = useLocalSearchParams<'/[siloKey]/suspended'>();
    const session = useSession();
    const profileKey = usePersonaKey();
    const locale = useLanguage();
    const isSuspended = useIsSuspended({ siloKey, profileKey, locale });
    const isSignedOut = !session.isPending && !session.data;
    const isUnsuspended = !!profileKey && isSuspended === false;
    const shouldGoBack = (isSignedOut || isUnsuspended) && router.canGoBack();

    useEffect(() => {
        if (shouldGoBack) {
            router.back();
        }
    }, [shouldGoBack]);

    if (shouldGoBack) {
        return <LoadingSpinnerContainer />;
    }

    if (isSignedOut) {
        return (
            <Redirect
                href={{
                    pathname: '/[siloKey]/login/one',
                    params: { siloKey },
                }}
            />
        );
    }

    if (isUnsuspended) {
        return (
            <Redirect
                href={{
                    pathname: '/[siloKey]/account/one',
                    params: { siloKey },
                }}
            />
        );
    }

    if (!isSuspended) {
        return <LoadingSpinnerContainer />;
    }

    return <LoginBlockedScreen />;
}
